import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import './Login.css';
import { authService } from '../../services/api';
import { beginOidc } from './oidcFlow';

// All identifiers see the same methods. Eligibility is verified by the server
// only when the user attempts a password or passkey ceremony.
const AdminLogin = () => {
  const { login, loginWithWebauthn, loginWithOidc } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState('email'); // 'email' | 'methods'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const methods = { password: true, webauthn: true };
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [oidcEnabled, setOidcEnabled] = useState(false);
  const [ssoOnly, setSsoOnly] = useState(false), [emergency, setEmergency] = useState(false);
  const completionStarted = useRef(false);

  useEffect(() => {
    let active = true;
    Promise.resolve(authService.oidcConfig()).then(value => { if (active) { setOidcEnabled(value?.enabled === true); setSsoOnly(value?.staffSsoOnly === true); } }).catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const result = new URLSearchParams(window.location.search).get('oidc');
    if (!result || completionStarted.current) return;
    completionStarted.current = true;
    window.history.replaceState(null, '', window.location.pathname);
    if (result !== 'complete') { setError('Staff sign-in did not complete. Start again.'); return; }
    setBusy(true);
    loginWithOidc().then(user => {
      navigate(user.isFirstLogin ? '/create-password' : user.requiresModeSelection ? '/choose-access' : user.userType === 'support' ? '/support' : '/admin/dashboard', { replace: true });
    }).catch(() => setError('Staff sign-in did not complete. Start again.')).finally(() => setBusy(false));
  }, [loginWithOidc, navigate]);

  const routeUser = (user) => {
    if (!user || !user.userType) {
      setError('Invalid user data received. Please contact support.');
      return;
    }
    if (user.isFirstLogin === true) {
      navigate('/create-password');
      return;
    }
    if (user.requiresModeSelection) { navigate('/choose-access'); return; }
    switch (user.userType) {
      case 'admin': navigate('/admin/dashboard'); break;
      case 'employee': navigate('/employee/calendar'); break;
      case 'customer': navigate('/customer/dashboard'); break;
      case 'support': navigate('/support'); break;
      default: setError(`Unknown user type: ${user.userType}`);
    }
  };

  const handleContinue = async (e) => {
    e.preventDefault();
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setError(t('auth.login.emailRequired', { defaultValue: 'Enter your e-mail first' }));
      return;
    }
    setError(null);
    setStep('methods');
  };

  const handlePassword = async (e) => {
    e.preventDefault();
    if (!password) {
      setError(t('auth.login.passwordRequired', { defaultValue: 'Password is required' }));
      return;
    }
    try {
      setBusy(true);
      setError(null);
      const user = await login(email, password);
      routeUser(user);
    } catch (err) {
      setError(err.code === 'LOGIN_LINK_REQUIRED' ? t('auth.access.passwordMoved') : (err.message || 'Login failed. Please check your credentials.'));
    } finally {
      setBusy(false);
    }
  };

  const handleWebauthn = async () => {
    try {
      setBusy(true);
      setError(null);
      const user = await loginWithWebauthn(email);
      routeUser(user);
    } catch (err) {
      setError(t('auth.adminLogin.helloFailed', {
        defaultValue: 'Windows Hello sign-in did not complete — use your password below. If Windows Hello is not set up on this device yet, sign in with your password and enrol it under Settings → Security.',
      }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-container">
      <h1 className="login-title">
        {t('auth.adminLogin.title', { defaultValue: 'Administrator sign-in' })}
      </h1>

      {error && <div className="login-alert" role="alert">{error}</div>}

      {oidcEnabled && <button type="button" className="submit-button" disabled={busy} onClick={async () => {
        try { setBusy(true); await beginOidc(); window.location.assign(authService.oidcStartUrl()); }
        catch (_) { setBusy(false); setError('Staff sign-in did not complete. Start again.'); }
      }}>Sign in with Vikar Experten</button>}

      {ssoOnly && !emergency && <button type="button" onClick={() => setEmergency(true)}>Emergency administrator login</button>}
      {(!ssoOnly || emergency) && step === 'email' && (
        <form className="login-form" onSubmit={handleContinue}>
          <div className="form-group">
            <label htmlFor="admin-email" className="form-label">{t('auth.login.email', { defaultValue: 'Email' })}</label>
            <input
              type="email"
              id="admin-email"
              className="form-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              autoComplete="username"
              autoFocus
            />
          </div>
          <button type="submit" className="submit-button" disabled={busy}>
            {busy
              ? t('auth.adminLogin.checking', { defaultValue: 'Checking…' })
              : t('auth.adminLogin.continue', { defaultValue: 'Continue' })}
          </button>
        </form>
      )}

      {(!ssoOnly || emergency) && step === 'methods' && (
        <>
          <p style={{ textAlign: 'center', margin: '0 0 1rem', color: 'var(--ve-ink-600)', fontSize: '0.95rem' }}>
            {email}{' '}
            <button
              type="button"
              onClick={() => { setStep('email'); setError(null); setPassword(''); }}
              style={{ background: 'none', border: 'none', color: 'var(--act-primary)', textDecoration: 'underline', cursor: 'pointer', fontSize: '0.85rem' }}
            >
              {t('auth.adminLogin.change', { defaultValue: 'change' })}
            </button>
          </p>

          {methods.webauthn && (
            <button
              type="button"
              className="submit-button"
              onClick={handleWebauthn}
              disabled={busy}
              style={{ marginBottom: '1rem' }}
            >
              {t('auth.adminLogin.hello', { defaultValue: 'Sign in with Windows Hello' })}
            </button>
          )}

          <form className="login-form" onSubmit={handlePassword}>
            <div className="form-group">
              <label htmlFor="admin-password" className="form-label">{t('auth.login.password', { defaultValue: 'Password' })}</label>
              <input
                type="password"
                id="admin-password"
                className="form-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                autoComplete="current-password"
                autoFocus={!methods.webauthn}
              />
            </div>
            <button type="submit" className="submit-button" disabled={busy}>
              {busy ? t('auth.login.signingIn', { defaultValue: 'Signing in…' }) : t('auth.login.signIn', { defaultValue: 'Sign in' })}
            </button>
          </form>

          {!methods.webauthn && (
            <p style={{ textAlign: 'center', margin: '1rem 0 0', color: 'var(--ve-ink-500)', fontSize: '0.8rem', lineHeight: 1.5 }}>
              {t('auth.adminLogin.helloHint', {
                defaultValue: 'Windows Hello appears here once it is enrolled for this account (sign in with your password, then Settings → Security).',
              })}
            </p>
          )}
        </>
      )}

      <Link to="/forgot-password" className="forgot-password-link">
        {t('auth.login.forgotPassword')}
      </Link>
      <Link to="/login" className="forgot-password-link">
        {t('auth.adminLogin.backToLogin', { defaultValue: 'Back to the normal sign-in page' })}
      </Link>
    </div>
  );
};

export default AdminLogin;
